import { useState, useRef, useEffect } from "react";
import styles from "../HealthChat.module.css";
import { MessageIcon, MicIcon, SendIcon } from "../icons";
import VoiceScreen from "./VoiceScreen";
import useVoiceChat from "../../../hooks/useVoiceChat";

async function translateToEnglish(text, sourceLang) {
  if (!text || sourceLang === "en") return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=en&dt=t&q=${encodeURIComponent(text)}`,
    );
    const data = await res.json();
    return data[0]?.map((item) => item[0]).join("") || text;
  } catch {
    return text;
  }
}

async function translateFromEnglish(text, targetLang) {
  if (!text || targetLang === "en") return text;
  try {
    const res = await fetch(
      `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`,
    );
    const data = await res.json();
    return data[0]?.map((item) => item[0]).join("") || text;
  } catch {
    return text;
  }
}

export default function ChatScreen({ t, tVoice, onSummary, lang, onBack }) {
  const [tab, setTab] = useState("chat");
  const [messages, setMessages] = useState([]);
  const [textInput, setTextInput] = useState("");
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  const RASA_URL =
    process.env.NEXT_PUBLIC_RASA_URL ||
    "https://femcare.avexa.net/chatbot/webhooks/rest/webhook";

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const {
    isListening,
    liveText,
    isSpeaking,
    micError,
    toggleMic,
    speak,
    speakAll,
    stopSpeaking,
    isSpeechSupported,
  } = useVoiceChat({
    lang,
    autoSend: true,
    silenceMs: 1800,
    onSendText: (capturedText) => sendMessage(capturedText),
  });

  const hasText = textInput.trim().length > 0;

  async function sendMessage(userText) {
    if (!userText?.trim() || loading) return;

    stopSpeaking();

    let displayUserText = userText;
    if (lang !== "en") {
      try {
        displayUserText = await translateFromEnglish(
          await translateToEnglish(userText, lang),
          lang,
        );
      } catch {
        displayUserText = userText;
      }
    }

    const userMsg = {
      id: Date.now(),
      from: "user",
      text: displayUserText,
      raw: userText,
    };

    setMessages((prev) => [...prev, userMsg]);
    setTextInput("");
    setLoading(true);

    try {
      const englishText = await translateToEnglish(userText, lang);

      const res = await fetch(RASA_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sender: `avexa-web-user-${lang}`,
          message: englishText,
          metadata: { originalLanguage: lang },
        }),
      });

      const data = await res.json();
      const rasaMsgs = [];

      for (const msg of data) {
        let translatedText = msg.text;
        if (msg.text && lang !== "en") {
          translatedText = await translateFromEnglish(msg.text, lang);
        }

        let translatedButtons = msg.buttons || [];
        if (msg.buttons && lang !== "en") {
          translatedButtons = await Promise.all(
            msg.buttons.map(async (b) => ({
              ...b,
              title: await translateFromEnglish(b.title, lang),
            })),
          );
        }

        rasaMsgs.push({
          id: Date.now() + Math.random(),
          from: "bot",
          text: translatedText,
          buttons: translatedButtons,
          custom: msg.custom || null,
        });
      }

      setMessages((prev) => [...prev, ...rasaMsgs]);

      speakAll(rasaMsgs);

      const customMsg = data.find((m) => m.custom?.type === "summary");
      if (customMsg) onSummary(customMsg.custom);
    } catch {
      const errText = t.errorMsg || "Something went wrong. Please try again.";
      setMessages((prev) => [
        ...prev,
        { id: Date.now(), from: "bot", text: errText },
      ]);
      speak(errText);
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className={styles.padding}>
        <button className={styles.selectDoctorBack} onClick={onBack}>
          ← Back
        </button>
      </div>

      {tab === "chat" && (
        <div className={styles.body}>
          <div className={styles.botBubble}>
            {t.welcome} <b>{t.welcomeBold}</b>.
            <br />
            {t.welcomeEnd}
          </div>

          {messages.map((msg, i) => (
            <div key={msg.id || i}>
              {msg.from === "bot" ? (
                <>
                  <div style={{ display: "flex", gap: 6 }}>
                    <div className={styles.botMsg} style={{ flex: 1 }}>
                      {msg.text}
                    </div>
                    {msg.text && (
                      <button onClick={() => speak(msg.text)}>🔊</button>
                    )}
                  </div>

                  {msg.buttons?.length > 0 && (
                    <div className={styles.btnGroup}>
                      {msg.buttons.map((b) => (
                        <button
                          key={b.title}
                          className={styles.optionBtn}
                          onClick={() => {
                            const clean = (b.payload || b.title)
                              .replace(/^\//, "")
                              .replace(/_/g, " ");
                            speak(b.title);
                            sendMessage(clean);
                          }}
                        >
                          {b.title}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <div className={styles.userMsg}>{msg.text}</div>
              )}
            </div>
          ))}

          {loading && (
            <div className={styles.typingDot}>
              <span />
              <span />
              <span />
            </div>
          )}

          {isSpeaking && (
            <div>
              🔊 Speaking...
              <button onClick={stopSpeaking}>✕</button>
            </div>
          )}

          <div ref={bottomRef} />
        </div>
      )}

      {tab === "voice" && <VoiceScreen t={tVoice} lang={lang} />}

      {tab === "chat" && (
        <>
          <div className={styles.inputWrap}>
            <input
              className={styles.input}
              placeholder={isListening ? "Listening…" : t.placeholder}
              value={isListening ? liveText : textInput}
              onChange={(e) => !isListening && setTextInput(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && !isListening && sendMessage(textInput)
              }
              readOnly={isListening}
            />

            {/*  Smart Toggle Button */}
            {!hasText && !isListening ? (
              <button
                className={styles.micIconBtn}
                onClick={toggleMic}
                disabled={!isSpeechSupported}
              >
                <MicIcon size={18} />
              </button>
            ) : (
              <button
                className={styles.sendBtn}
                onClick={() =>
                  isListening ? toggleMic() : sendMessage(textInput)
                }
              >
                <SendIcon size={15} color="#fff" />
              </button>
            )}
          </div>

          {isListening && <div>🎤 Listening...</div>}
          {micError && <div style={{ color: "red" }}>{micError}</div>}
        </>
      )}

      {/* <div className={styles.tabs}>
     <div
       className={tab === "chat" ? styles.tabActive : styles.tab}
       onClick={() => setTab("chat")}
     >
       <MessageIcon size={14} /> {t.tabChat}
     </div>
     <div
       className={tab === "voice" ? styles.tabActive : styles.tab}
       onClick={() => setTab("voice")}
     >
       <MicIcon size={14} /> {t.tabVoice}
     </div>
   </div> */}
    </>
  );
}

// import { useState, useRef, useEffect } from "react";
// import styles from "../HealthChat.module.css";
// import { MessageIcon, MicIcon, SendIcon } from "../icons";
// import VoiceScreen from "./VoiceScreen";
// import useVoiceChat from "../../../hooks/useVoiceChat";

// async function translateToEnglish(text, sourceLang) {
//   if (!text || sourceLang === "en") return text;

//   try {
//     const res = await fetch(
//       `https://translate.googleapis.com/translate_a/single?client=gtx&sl=${sourceLang}&tl=en&dt=t&q=${encodeURIComponent(text)}`
//     );
//     const data = await res.json();
//     return data[0]?.map((item) => item[0]).join("") || text;
//   } catch {
//     return text;
//   }
// }

// async function translateFromEnglish(text, targetLang) {
//   if (!text || targetLang === "en") return text;

//   try {
//     const res = await fetch(
//       `https://translate.googleapis.com/translate_a/single?client=gtx&sl=en&tl=${targetLang}&dt=t&q=${encodeURIComponent(text)}`
//     );
//     const data = await res.json();
//     return data[0]?.map((item) => item[0]).join("") || text;
//   } catch {
//     return text;
//   }
// }

// export default function ChatScreen({ t, tVoice, onSummary, lang }) {
//   const [tab, setTab] = useState("chat");
//   const [messages, setMessages] = useState([]);
//   const [textInput, setTextInput] = useState("");
//   const [loading, setLoading] = useState(false);
//   const bottomRef = useRef(null);

//   const RASA_URL =
//     process.env.NEXT_PUBLIC_RASA_URL ||
//     "https://femcare.avexa.net/chatbot/webhooks/rest/webhook";

//   useEffect(() => {
//     bottomRef.current?.scrollIntoView({ behavior: "smooth" });
//   }, [messages, loading]);

//   const {
//     isListening,
//     liveText,
//     isSpeaking,
//     micError,
//     toggleMic,
//     speak,
//     stopSpeaking,
//     isSpeechSupported,
//   } = useVoiceChat({
//     lang,
//     autoSend: true,
//     silenceMs: 1800,
//     onSendText: (capturedText) => sendMessage(capturedText),
//   });

//   async function speakAllMessages(messages) {
//     stopSpeaking();

//     for (const msg of messages) {
//       // Speak message text
//       if (msg.text) {
//         await new Promise((resolve) => {
//           const utter = new SpeechSynthesisUtterance(msg.text);

//           // optional: keep your existing voice selection logic here

//           utter.onend = resolve;
//           speechSynthesis.speak(utter);
//         });
//       }

//       //  Speak buttons (options)
//       if (msg.buttons && msg.buttons.length > 0) {
//         const optionsText = msg.buttons.map((b) => b.title).join(", ");

//         await new Promise((resolve) => {
//           const utter = new SpeechSynthesisUtterance(
//             `Options are: ${optionsText}`
//           );

//           utter.onend = resolve;
//           speechSynthesis.speak(utter);
//         });
//       }
//     }
//   }

//   async function sendMessage(userText) {
//     if (!userText?.trim() || loading) return;

//     stopSpeaking();

//     let displayUserText = userText;

//     //  Translate for display if needed
//     if (lang !== "en") {
//       try {
//         displayUserText = await translateFromEnglish(
//           await translateToEnglish(userText, lang),
//           lang
//         );
//       } catch {
//         displayUserText = userText;
//       }
//     }

//     const userMsg = {
//       id: Date.now(),
//       from: "user",
//       text: displayUserText,
//       raw: userText,
//     };

//     setMessages((prev) => [...prev, userMsg]);
//     setTextInput("");
//     setLoading(true);

//     try {
//       // 1. Translate user input → English
//       const englishText = await translateToEnglish(userText, lang);

//       // 2. Send to Rasa
//       const res = await fetch(RASA_URL, {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({
//           sender: `avexa-web-user-${lang}`,
//           message: englishText,
//           metadata: { originalLanguage: lang },
//         }),
//       });

//       const data = await res.json();

//       // 3. Translate response + buttons
//       const rasaMsgs = [];

//       for (const msg of data) {
//         let translatedText = msg.text;

//         //  Translate message text
//         if (msg.text && lang !== "en") {
//           translatedText = await translateFromEnglish(msg.text, lang);
//         }

//         //  Translate buttons (YOUR CODE FIXED)
//         let translatedButtons = msg.buttons || [];

//         if (msg.buttons && lang !== "en") {
//           translatedButtons = await Promise.all(
//             msg.buttons.map(async (b) => ({
//               ...b,
//               title: await translateFromEnglish(b.title, lang),
//             }))
//           );
//         }

//         rasaMsgs.push({
//           id: Date.now() + Math.random(),
//           from: "bot",
//           text: translatedText,
//           buttons: translatedButtons,
//           custom: msg.custom || null,
//         });
//       }

//       setMessages((prev) => [...prev, ...rasaMsgs]);

//       // 4. Speak first response
//       // const firstText = rasaMsgs.find((m) => m.text?.trim());
//       // if (firstText) speak(firstText.text);
//       speakAllMessages(rasaMsgs);

//       // 5. Summary handling
//       const customMsg = data.find((m) => m.custom?.type === "summary");
//       if (customMsg) onSummary(customMsg.custom);

//     } catch {
//       const errText = t.errorMsg || "Something went wrong. Please try again.";

//       setMessages((prev) => [
//         ...prev,
//         { id: Date.now(), from: "bot", text: errText },
//       ]);

//       speak(errText);
//     } finally {
//       setLoading(false);
//     }
//   }

//   return (
//     <>
//       {tab === "chat" && (
//         <div className={styles.body}>
//           <div className={styles.botBubble}>
//             {t.welcome} <b>{t.welcomeBold}</b>.
//             <br />
//             {t.welcomeEnd}
//           </div>

//           {messages.map((msg, i) => (
//             <div key={msg.id || i}>
//               {msg.from === "bot" ? (
//                 <>
//                   <div style={{ display: "flex", alignItems: "flex-start", gap: 6 }}>
//                     <div className={styles.botMsg} style={{ flex: 1 }}>
//                       {msg.text}
//                     </div>

//                     {msg.text && (
//                       <button
//                         onClick={() => speak(msg.text)}
//                         title="Read aloud"
//                         style={{
//                           flexShrink: 0,
//                           marginTop: 6,
//                           width: 26,
//                           height: 26,
//                           borderRadius: "50%",
//                           border: "0.5px solid var(--color-border-secondary)",
//                           background: "var(--color-background-primary)",
//                           cursor: "pointer",
//                           display: "flex",
//                           alignItems: "center",
//                           justifyContent: "center",
//                           fontSize: 12,
//                         }}
//                       >
//                         🔊
//                       </button>
//                     )}
//                   </div>

//                   {msg.buttons?.length > 0 && (
//                     <div className={styles.btnGroup}>
//                       {msg.buttons.map((b) => (
//                         <button
//                           key={b.title}
//                           className={styles.optionBtn}
//                           // onClick={() => {
//                           //   const clean = (b.payload || b.title)
//                           //     .replace(/^\//, "")
//                           //     .replace(/_/g, " ");
//                           //   sendMessage(clean);
//                           //   setMessages((prev) =>
//                           //     prev.map((m, idx) =>
//                           //       idx === i ? { ...m, buttons: [] } : m
//                           //     )
//                           //   );
//                           // }}
//                           onClick={async () => {
//                             const clean = (b.payload || b.title)
//                               .replace(/^\//, "")
//                               .replace(/_/g, " ");

//                             // 🔊 Speak selected option (translated if needed)
//                             let speakText = b.title;

//                             if (lang !== "en") {
//                               try {
//                                 speakText = await translateFromEnglish(b.title, lang);
//                               } catch {
//                                 speakText = b.title;
//                               }
//                             }

//                             speak(speakText);   //  speak immediately

//                             sendMessage(clean);

//                             setMessages((prev) =>
//                               prev.map((m, idx) =>
//                                 idx === i ? { ...m, buttons: [] } : m
//                               )
//                             );
//                           }}
//                         >
//                           {b.title}
//                         </button>
//                       ))}
//                     </div>
//                   )}
//                 </>
//               ) : (
//                 <div className={styles.userMsg}>{msg.text}</div>
//               )}
//             </div>
//           ))}

//           {loading && (
//             <div className={styles.typingDot}>
//               <span /><span /><span />
//             </div>
//           )}

//           {isSpeaking && (
//             <div
//               style={{
//                 display: "inline-flex",
//                 alignItems: "center",
//                 gap: 7,
//                 alignSelf: "flex-start",
//                 padding: "6px 12px",
//                 borderRadius: 20,
//                 background: "#eaf0ff",
//                 fontSize: 12,
//                 color: "#132573",
//                 fontWeight: 500,
//               }}
//             >
//               <span style={{ fontSize: 14 }}>🔊</span>
//               Speaking…
//               <button onClick={stopSpeaking}>✕</button>
//             </div>
//           )}

//           <div ref={bottomRef} />
//         </div>
//       )}

//       {tab === "voice" && (
//         <VoiceScreen
//           t={tVoice}
//           lang={lang}
//           onEnd={() => setTab("chat")}
//           onSendMessage={(text) => { sendMessage(text); setTab("chat"); }}
//         />
//       )}

//       {tab === "chat" && (
//         <>
//           <div className={styles.inputWrap}>
//             <button
//               className={styles.micIconBtn}
//               onClick={toggleMic}
//               disabled={!isSpeechSupported}
//             >
//               <MicIcon size={18} />
//             </button>

//             <input
//               className={styles.input}
//               placeholder={isListening ? "Listening…" : t.placeholder}
//               value={isListening ? liveText : textInput}
//               onChange={(e) => !isListening && setTextInput(e.target.value)}
//               readOnly={isListening}
//             />

//             <button
//               className={styles.sendBtn}
//               onClick={() =>
//                 isListening ? toggleMic() : sendMessage(textInput)
//               }
//             >
//               <SendIcon size={15} color="#fff" />
//             </button>
//           </div>

//           {isListening && <div>🎤 Listening...</div>}
//           {micError && <div>{micError}</div>}
//         </>
//       )}

//       <div className={styles.tabs}>
//         <div
//           className={tab === "chat" ? styles.tabActive : styles.tab}
//           onClick={() => setTab("chat")}
//         >
//           <MessageIcon size={14} /> {t.tabChat}
//         </div>
//         <div
//           className={tab === "voice" ? styles.tabActive : styles.tab}
//           onClick={() => setTab("voice")}
//         >
//           <MicIcon size={14} /> {t.tabVoice}
//         </div>
//       </div>
//     </>
//   );
// }
